import { CustomerType } from '@api/generated/graphql';
import getErrorsFromFormState from '@components/forms/util/getErrorMessagesFromFormState';
import {
  Banner,
  ButtonToggle,
  Divider,
  Form,
  Headline,
  Label,
  Stack,
} from '@servicetitan/design-system';
import { useMemo } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import {
  CreateCustomerFormProps,
  CreateCustomerFormValues,
} from './CreateCustomerFormDefinitions';
import BillingForm from './components/BillingForm/BillingForm';
import strings from './strings';
import ContactMethodForm from '@components/forms/components/ContactMethodForm/ContactMethodForm';
import AddressFormManager from '@components/forms/components/AddressFormManager';
import { inboxLocators } from '@locators/inbox';

// used to key the main customer form fields
const CUSTOMER_FORM_PREFIX = 'customer';

/**
 * Renders a form for creating a customer.
 * NOTE: This form component relies on a form provider to provide the form context.
 * Please read @src/components/forms/README.md for more information on how this form was built.
 * @param onSubmit - The function to be called when the form is submitted.
 */
const CreateCustomerForm = ({ onSubmit }: CreateCustomerFormProps) => {
  const {
    register,
    getValues,
    setValue,
    trigger,
    control,
    formState: { errors },
  } = useFormContext<CreateCustomerFormValues>();

  const fieldArray = useFieldArray<CreateCustomerFormValues>({
    control,
    name: 'customer.contacts',
  });

  const { update, append, remove, fields } = fieldArray;

  const errorSummary = useMemo(() => getErrorsFromFormState(errors), [errors]);

  return (
    <Form onSubmit={onSubmit}>
      {errorSummary && !!errorSummary.length && (
        <Banner
          className='m-b-4'
          status='critical'
          icon
          title={strings.errorBanner.title}
        >
          <ul>
            {errorSummary.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </Banner>
      )}
      <Stack direction='column' spacing={1} className='m-b-3'>
        <Label>{strings.labels.customerType}</Label>
        <ButtonToggle
          size='small'
          options={[
            {
              text: strings.labels.residential,
              value: CustomerType.Residential,
              iconName: 'home',
            },
            {
              text: strings.labels.commercial,
              value: CustomerType.Commercial,
              iconName: 'business',
            },
          ]}
          value={getValues('type')}
          onChange={(val: CustomerType) => {
            setValue('type', val, { shouldDirty: true });
            trigger('type');
          }}
        />
      </Stack>
      <Headline className='m-b-3' size='small'>
        {strings.headlines.locationName}
      </Headline>
      <Form.Input
        required
        id={inboxLocators.customerSearch.createFormFields.locationName}
        label={strings.labels.locationName}
        placeholder={strings.placeholders.locationName}
        small
        defaultValue={getValues('customer.name')}
        error={errors.customer?.name?.message}
        {...register(`customer.name`)}
        onBlur={() => trigger(`customer.name`)}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setValue(`customer.name`, e.target.value, { shouldDirty: true })
        }
      />
      <AddressFormManager formFieldPrefix={CUSTOMER_FORM_PREFIX} />
      <ContactMethodForm
        formFieldPrefix={CUSTOMER_FORM_PREFIX}
        append={append}
        update={update}
        remove={remove}
        contactMethodFields={fields}
      />
      <Divider className='m-x--3' spacing={3} />
      <BillingForm />
    </Form>
  );
};

export default CreateCustomerForm;
